import Api from "api/Api";
import Loader from "layout/atoms/Loader";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectCompanyInfo } from "store/companySlice";
import { fetchUser, selectUserCompanies } from "store/userSlice";

import { ReactComponent as EmptyStateSvg } from "assets/illustrations/empty-state-notifications.svg";
import Icon from "layout/atoms/Icon";
import { openModal } from "store/modalsSlice";
import {
  DropdownPanel,
  EmptyMessage,
  EmptyState,
  EmptyTitle,
  MarkAllAsReadButton,
  NotificationContent,
  NotificationContentWrapper,
  NotificationItem,
  NotificationList,
  NotificationsContainer,
  NotificationTime,
  TriggerWrapper,
  UnreadBadge,
  UnreadDot,
  Wrapper,
} from "./styles";

const api = new Api();

const Notifications = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const containerRef = useRef(null);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const companyInfo = useSelector(selectCompanyInfo);
  const userCompanies = useSelector(selectUserCompanies);

  // Get unread count for current company from user companies
  const currentCompany = userCompanies.find((c) => c.id === companyInfo.id);
  const unreadCount = currentCompany?.unreads || 0;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleToggle = () => {
    if (!open) fetchNotifications();
    setOpen((prev) => !prev);
  };

  const fetchNotifications = async () => {
    if (!companyInfo.id) return;
    setLoading(true);
    try {
      const response = await api.getNotifications(companyInfo.id, 0, 10);
      setNotifications(response.content || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationMessage = (notification) => {
    const { type, payload } = notification;

    switch (type) {
      case "IDEA_CREATED":
        return `New idea: ${payload.ideaTitle}`;
      case "IDEA_COMMENT":
        return `New comment on "${payload.ideaTitle}": ${payload.commentMessage}`;
      default:
        return "New notification";
    }
  };

  const getDisplayCount = () => {
    if (unreadCount === 0) return null;
    if (unreadCount > 9) return "9+";
    return unreadCount.toString();
  };

  const markAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
    );
  };

  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0 || !companyInfo.id) return;

    setMarkingAsRead(true);
    try {
      await api.markAllNotificationsAsRead(companyInfo.id, notifications[0].id);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      dispatch(fetchUser());
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    } finally {
      setMarkingAsRead(false);
    }
  };

  const handleNotificationClick = (notification) => {
    const { type, payload } = notification;

    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (type === "IDEA_CREATED" || type === "IDEA_COMMENT") {
      const boardId = payload.boardId;
      const ideaId = payload.ideaId;

      if (boardId && ideaId) {
        navigate(`/admin/boards/${boardId}/list`);
        dispatch(openModal({ name: "ideaModal", data: { ideaId, boardId } }));
        setOpen(false);
      }
    }
  };

  return (
    <NotificationsContainer ref={containerRef}>
      <TriggerWrapper onClick={handleToggle}>
        {children}
        {getDisplayCount() && <UnreadBadge>{getDisplayCount()}</UnreadBadge>}
      </TriggerWrapper>
      {open && (
        <DropdownPanel>
          <Wrapper>
            {loading ? (
              <EmptyState>
                <Loader size={30} />
              </EmptyState>
            ) : notifications.length === 0 ? (
              <EmptyState>
                <Icon src={EmptyStateSvg} size="150px" />
                <EmptyTitle>No notifications yet</EmptyTitle>
                <EmptyMessage>
                  You're all caught up! We'll notify you when there's something
                  new.
                </EmptyMessage>
              </EmptyState>
            ) : (
              <>
                <NotificationList>
                  {notifications.map((notification) => {
                    const isUnread = !notification.read;
                    return (
                      <NotificationItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <UnreadDot isUnread={isUnread} />
                        <NotificationContentWrapper>
                          <NotificationContent isUnread={isUnread}>
                            {getNotificationMessage(notification)}
                          </NotificationContent>
                          <NotificationTime>
                            {formatTime(notification.createdAt)}
                          </NotificationTime>
                        </NotificationContentWrapper>
                      </NotificationItem>
                    );
                  })}
                </NotificationList>
                <MarkAllAsReadButton
                  onClick={handleMarkAllAsRead}
                  disabled={markingAsRead || notifications.length === 0}
                >
                  {markingAsRead ? "Marking as read..." : "Mark all as read"}
                </MarkAllAsReadButton>
              </>
            )}
          </Wrapper>
        </DropdownPanel>
      )}
    </NotificationsContainer>
  );
};

export default Notifications;
